import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dagre from '@dagrejs/dagre'
import {
  BookOpen,
  Calendar,
  Circle,
  Download,
  ExternalLink,
  Eye,
  Flag,
  MapPin,
  Save,
  Sparkles,
  Sword,
  User,
  type LucideIcon,
} from 'lucide-react'
import {
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
  getSmoothStepPath,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useTranslation } from 'react-i18next'
import { downloadBlob } from '@/lib/download'
import { toast } from 'sonner'
import { characterApi } from '@/api/characterApi'
import { friendlyMessage } from '@/api/http'
import {
  fetchCausality,
  fetchEntityEdges,
  fetchGraph,
  resolveForeshadowEdge,
  type CausalEvent,
  type EntityEdge,
} from '@/api/relationApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Slider } from '@/components/ui/slider'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { Character, CharacterInput } from '@/types/nf'

interface EntityRelationGraphProps {
  projectId: string
  branchId?: string
  maxChapter?: number
  onSelectEntity?: (entityType: string, entityId: string) => void
}

export type EntityNameIndex = Map<string, string>

const NODE_W = 190
const SVG_PADDING = 48
const NODE_H = 54
const LABEL_H = 24
const LABEL_PAD_X = 12
const LABEL_CHAR_W = 8
const LAYOUT_EPSILON = 0.5

export const ENTITY_NODE_COLORS: Record<string, { border: string; background: string; text: string }> = {
  character: { border: '#2563eb', background: '#dbeafe', text: '#1e3a8a' },
  faction: { border: '#7c3aed', background: '#ede9fe', text: '#4c1d95' },
  item: { border: '#d97706', background: '#fef3c7', text: '#78350f' },
  location: { border: '#059669', background: '#d1fae5', text: '#064e3b' },
  power: { border: '#dc2626', background: '#fee2e2', text: '#7f1d1d' },
  event: { border: '#0891b2', background: '#cffafe', text: '#164e63' },
  foreshadow: { border: '#db2777', background: '#fce7f3', text: '#831843' },
  chapter: { border: '#4b5563', background: '#f3f4f6', text: '#111827' },
  unknown: { border: '#64748b', background: '#f1f5f9', text: '#0f172a' },
}

export const EVENT_NODE_COLORS: Record<string, { border: string; background: string; text: string }> = {
  character_introduce: { border: '#2563eb', background: '#dbeafe', text: '#1e3a8a' },
  character_evolve: { border: '#1d4ed8', background: '#dbeafe', text: '#1e3a8a' },
  location_introduce: { border: '#059669', background: '#d1fae5', text: '#064e3b' },
  location_update: { border: '#0d9488', background: '#ccfbf1', text: '#134e4a' },
  plot_advance: { border: '#d946ef', background: '#fae8ff', text: '#701a75' },
  plot_resolve: { border: '#9333ea', background: '#f3e8ff', text: '#581c87' },
  timeline_event: { border: '#0891b2', background: '#cffafe', text: '#164e63' },
  chapter_control_card: { border: '#4b5563', background: '#f3f4f6', text: '#111827' },
  power_introduce: { border: '#dc2626', background: '#fee2e2', text: '#7f1d1d' },
  power_cultivate: { border: '#f59e0b', background: '#fef3c7', text: '#78350f' },
  power_breakthrough: { border: '#ea580c', background: '#ffedd5', text: '#7c2d12' },
  faction_introduce: { border: '#7c3aed', background: '#ede9fe', text: '#4c1d95' },
  faction_relation: { border: '#6d28d9', background: '#ede9fe', text: '#4c1d95' },
  entity_edge: { border: '#64748b', background: '#f1f5f9', text: '#0f172a' },
  item_introduce: { border: '#d97706', background: '#fef3c7', text: '#78350f' },
  item_transfer: { border: '#ca8a04', background: '#fef9c3', text: '#713f12' },
  item_destroy: { border: '#b91c1c', background: '#fee2e2', text: '#7f1d1d' },
}

export const ENTITY_EDGE_STYLES: Record<string, { color: string; dashed?: boolean; marker: MarkerType }> = {
  master_of: { color: '#6366f1', marker: MarkerType.ArrowClosed },
  disciple_of: { color: '#6366f1', marker: MarkerType.ArrowClosed },
  mentor: { color: '#6366f1', marker: MarkerType.ArrowClosed },
  rival_of: { color: '#ef4444', dashed: true, marker: MarkerType.ArrowClosed },
  war_with: { color: '#ef4444', dashed: true, marker: MarkerType.ArrowClosed },
  combat_with: { color: '#ef4444', dashed: true, marker: MarkerType.ArrowClosed },
  hostile: { color: '#ef4444', dashed: true, marker: MarkerType.ArrowClosed },
  enemy: { color: '#ef4444', dashed: true, marker: MarkerType.ArrowClosed },
  ally_of: { color: '#22c55e', marker: MarkerType.ArrowClosed },
  ally_with: { color: '#22c55e', marker: MarkerType.ArrowClosed },
  allied: { color: '#22c55e', marker: MarkerType.ArrowClosed },
  ally: { color: '#22c55e', marker: MarkerType.ArrowClosed },
  vassal_of: { color: '#22c55e', marker: MarkerType.ArrowClosed },
  lover_of: { color: '#ec4899', dashed: true, marker: MarkerType.ArrowClosed },
  loves: { color: '#ec4899', dashed: true, marker: MarkerType.ArrowClosed },
  family_of: { color: '#ec4899', dashed: true, marker: MarkerType.ArrowClosed },
  member_of: { color: '#8b5cf6', marker: MarkerType.ArrowClosed },
  owns: { color: '#8b5cf6', marker: MarkerType.ArrowClosed },
  holds: { color: '#8b5cf6', marker: MarkerType.ArrowClosed },
  wields: { color: '#8b5cf6', marker: MarkerType.ArrowClosed },
  located_at: { color: '#14b8a6', marker: MarkerType.ArrowClosed },
  contains: { color: '#14b8a6', marker: MarkerType.ArrowClosed },
  adjacent_to: { color: '#14b8a6', marker: MarkerType.ArrowClosed },
  cultivates: { color: '#f59e0b', marker: MarkerType.ArrowClosed },
  foreshadows: { color: '#d946ef', dashed: true, marker: MarkerType.ArrowClosed },
  causes: { color: '#d946ef', dashed: true, marker: MarkerType.ArrowClosed },
  default: { color: '#64748b', marker: MarkerType.ArrowClosed },
}

export const ENTITY_ICON_PATHS: Record<string, string[]> = {
  character: [
    'M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2',
    'M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8',
  ],
  faction: [
    'M4 22V4a1 1 0 0 1 .4-.8A6 6 0 0 1 8 2c3 0 5 2 7.333 2q2 0 3.067-.8A1 1 0 0 1 20 4v10a1 1 0 0 1-.4.8A6 6 0 0 1 16 16c-3 0-5-2-8-2a6 6 0 0 0-4 1.528',
  ],
  item: [
    'm11 19-6-6',
    'm5 21-2-2',
    'm8 16-4 4',
    'M9.5 17.5 21 6V3h-3L6.5 14.5',
  ],
  location: [
    'M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0',
    'M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6',
  ],
  power: [
    'M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z',
    'M20 2v4',
    'M22 4h-4',
    'M4 18a2 2 0 1 0 0 4 2 2 0 0 0 0-4',
  ],
  event: ['M8 2v4', 'M16 2v4', 'M3 10h18', 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2'],
  foreshadow: [
    'M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0',
    'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6',
  ],
  chapter: [
    'M12 7v14',
    'M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z',
  ],
  unknown: ['M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20'],
}

const ENTITY_ICONS: Record<string, LucideIcon> = {
  character: User,
  faction: Flag,
  item: Sword,
  location: MapPin,
  power: Sparkles,
  event: Calendar,
  foreshadow: Eye,
  chapter: BookOpen,
  unknown: Circle,
}

export function getEntityNodeId(type: string, id: string) {
  return `${type}:${id}`
}

function getNodePalette(type: string) {
  return ENTITY_NODE_COLORS[type] ?? EVENT_NODE_COLORS[type] ?? ENTITY_NODE_COLORS.unknown
}

function getEdgePalette(type: string) {
  return ENTITY_EDGE_STYLES[type] ?? ENTITY_EDGE_STYLES.default
}

export interface GraphLabelBox {
  edgeId: string
  source: string
  target: string
  x: number
  y: number
  width: number
  height: number
}

export interface GraphLayoutIssues {
  nodeOverlaps: Array<{ a: string; b: string }>
  labelOcclusions: Array<{ edgeId: string; nodeId: string }>
  danglingEdges: Array<{ edgeId: string; source: string; target: string }>
}

type DagreLayoutResult = {
  positions: Map<string, { x: number; y: number }>
  labelBoxes: GraphLabelBox[]
}

type GraphLayoutEdge = {
  id: string
  source: string
  target: string
  label: string
}

function estimateLabelWidth(label: string) {
  return Math.max(36, label.length * LABEL_CHAR_W + LABEL_PAD_X * 2)
}

function layoutGraph(ids: string[], graphEdges: GraphLayoutEdge[]): DagreLayoutResult {
  const graph = new dagre.graphlib.Graph({ multigraph: true })
  graph.setGraph({ rankdir: 'LR', nodesep: 50, ranksep: 90 })
  graph.setDefaultEdgeLabel(() => ({}))

  for (const id of ids) {
    graph.setNode(id, { width: NODE_W, height: NODE_H })
  }

  for (const edge of graphEdges) {
    const width = estimateLabelWidth(edge.label)
    graph.setEdge(edge.source, edge.target, { width, height: LABEL_H, labelpos: 'c' }, edge.id)
  }

  dagre.layout(graph)

  const positions = new Map<string, { x: number; y: number }>()
  for (const id of ids) {
    const node = graph.node(id) as { x?: number; y?: number } | undefined
    positions.set(id, {
      x: (node?.x ?? NODE_W / 2) - NODE_W / 2,
      y: (node?.y ?? NODE_H / 2) - NODE_H / 2,
    })
  }

  const labelBoxes = graphEdges.map((edge) => {
    const label = graph.edge({ v: edge.source, w: edge.target, name: edge.id }) as
      | { x?: number; y?: number; width?: number; height?: number }
      | undefined
    const width = label?.width ?? estimateLabelWidth(edge.label)
    const height = label?.height ?? LABEL_H
    const centerX = label?.x ?? 0
    const centerY = label?.y ?? 0
    return {
      edgeId: edge.id,
      source: edge.source,
      target: edge.target,
      x: centerX - width / 2,
      y: centerY - height / 2,
      width,
      height,
    }
  })

  return { positions, labelBoxes }
}

function rectanglesOverlap(
  left: { x: number; y: number; width: number; height: number },
  right: { x: number; y: number; width: number; height: number },
  epsilon = LAYOUT_EPSILON,
) {
  return (
    left.x + left.width > right.x + epsilon &&
    right.x + right.width > left.x + epsilon &&
    left.y + left.height > right.y + epsilon &&
    right.y + right.height > left.y + epsilon
  )
}

export function detectGraphLayoutIssues(nodes: Node[], edges: Edge[], labelBoxes: GraphLabelBox[]): GraphLayoutIssues {
  const nodeById = new Map(nodes.map((node) => [node.id, node]))
  const nodeRects = nodes.map((node) => {
    const size = getNodeSize(node)
    return { id: node.id, x: node.position.x, y: node.position.y, width: size.width, height: size.height }
  })
  const nodeOverlaps: GraphLayoutIssues['nodeOverlaps'] = []
  const labelOcclusions: GraphLayoutIssues['labelOcclusions'] = []
  const danglingEdges: GraphLayoutIssues['danglingEdges'] = []

  for (let leftIndex = 0; leftIndex < nodeRects.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < nodeRects.length; rightIndex += 1) {
      const left = nodeRects[leftIndex]
      const right = nodeRects[rightIndex]
      if (rectanglesOverlap(left, right)) nodeOverlaps.push({ a: left.id, b: right.id })
    }
  }

  for (const labelBox of labelBoxes) {
    for (const nodeRect of nodeRects) {
      if (rectanglesOverlap(labelBox, nodeRect)) labelOcclusions.push({ edgeId: labelBox.edgeId, nodeId: nodeRect.id })
    }
  }

  for (const edge of edges) {
    if (!nodeById.has(edge.source) || !nodeById.has(edge.target)) {
      danglingEdges.push({ edgeId: edge.id, source: edge.source, target: edge.target })
    }
  }

  return { nodeOverlaps, labelOcclusions, danglingEdges }
}

type RelationGraphNodeData = { label: string; entityType: string; entityId: string }

export interface CharacterCanonFormValues {
  name: string
  aliasesText: string
  role: string
  personality: string
  motivation: string
  realm: string
  realmLevel: string
  background?: string
  appearance?: string
  status?: string
  notes?: string
}

function normalizeOptionalText(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? ''
  return trimmed ? trimmed : null
}

function parseAliasText(value: string) {
  return value
    .split(/[,，\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

export function buildCharacterCanonPatch(values: CharacterCanonFormValues): CharacterInput {
  const realmLevelText = values.realmLevel.trim()
  const realmLevel = Number(realmLevelText)
  const patch: CharacterInput = {
    name: values.name.trim(),
    aliases: parseAliasText(values.aliasesText),
    role: normalizeOptionalText(values.role),
    personality: normalizeOptionalText(values.personality),
    motivation: normalizeOptionalText(values.motivation),
    realm: normalizeOptionalText(values.realm),
    realm_level: realmLevelText && Number.isFinite(realmLevel) ? realmLevel : null,
  }
  if (values.background !== undefined) patch.background = normalizeOptionalText(values.background)
  if (values.appearance !== undefined) patch.appearance = normalizeOptionalText(values.appearance)
  if (values.status !== undefined) patch.status = normalizeOptionalText(values.status)
  if (values.notes !== undefined) patch.notes = normalizeOptionalText(values.notes)
  return patch
}

export function getNodeRelatedEdges(entityEdges: EntityEdge[], entityType: string, entityId: string) {
  return entityEdges.filter(
    (edge) =>
      (edge.source_type === entityType && edge.source_id === entityId) ||
      (edge.target_type === entityType && edge.target_id === entityId),
  )
}

function resolveEntityDisplayName(names: EntityNameIndex, entityId: string) {
  return names.get(entityId) ?? entityId
}

export function getRelatedEdgeLabel(edge: EntityEdge, names: EntityNameIndex) {
  const source = resolveEntityDisplayName(names, edge.source_id)
  const target = resolveEntityDisplayName(names, edge.target_id)
  return `${source} →${edge.edge_type}→ ${target}`
}

export function sortForeshadowEdges(edges: EntityEdge[]) {
  return [...edges].sort((left, right) => {
    const leftResolved = left.end_chapter !== null
    const rightResolved = right.end_chapter !== null
    if (leftResolved !== rightResolved) return leftResolved ? 1 : -1
    if (!leftResolved) return (left.set_chapter ?? Number.MAX_SAFE_INTEGER) - (right.set_chapter ?? Number.MAX_SAFE_INTEGER)
    return (right.end_chapter ?? 0) - (left.end_chapter ?? 0)
  })
}

export function getForeshadowSummary(edges: EntityEdge[]) {
  return {
    total: edges.length,
    unresolved: edges.filter((edge) => edge.end_chapter === null).length,
  }
}

function buildGraphNode(id: string, entityType: string, entityId: string, label: string, position: { x: number; y: number }) {
  const palette = getNodePalette(entityType)
  return {
    id,
    type: 'entity',
    position,
    data: {
      label,
      entityType,
      entityId,
    },
    style: {
      width: NODE_W,
      height: NODE_H,
      borderRadius: 8,
      border: `1px solid ${palette.border}`,
      background: palette.background,
      color: palette.text,
      fontSize: 12,
      fontWeight: 600,
    },
  } satisfies Node<RelationGraphNodeData>
}

function buildGraphEdge(input: {
  id: string
  source: string
  target: string
  label: string
  edgeType: string
  weight?: number | null
  labelBox?: GraphLabelBox
}) {
  const palette = getEdgePalette(input.edgeType)
  return {
    id: input.id,
    source: input.source,
    target: input.target,
    type: 'dagreSmoothstep',
    label: input.label,
    data: {
      labelBox: input.labelBox,
    },
    markerEnd: { type: palette.marker, color: palette.color },
    style: {
      stroke: palette.color,
      strokeWidth: Math.max(1, Math.min(4, Math.round((input.weight ?? 0.7) * 3))),
      strokeDasharray: palette.dashed ? '6 4' : undefined,
    },
    labelStyle: {
      fill: palette.color,
      fontSize: 11,
      fontWeight: 600,
    },
    labelBgStyle: {
      fill: 'rgba(255,255,255,0.86)',
      stroke: palette.color,
      strokeWidth: 0.5,
    },
    labelBgPadding: [6, 3],
    labelBgBorderRadius: 4,
  } satisfies Edge
}

export function buildEntityRelationGraphModel(entityEdges: EntityEdge[], names: EntityNameIndex) {
  const nodeTypes = new Map<string, string>()
  for (const edge of entityEdges) {
    nodeTypes.set(edge.source_id, edge.source_type)
    nodeTypes.set(edge.target_id, edge.target_type)
  }

  const ids = [...nodeTypes.keys()].sort((left, right) => {
    const typeCompare = (nodeTypes.get(left) ?? '').localeCompare(nodeTypes.get(right) ?? '')
    return typeCompare === 0 ? left.localeCompare(right) : typeCompare
  })
  const layoutEdges: GraphLayoutEdge[] = entityEdges.map((edge) => ({
    id: edge.id,
    source: edge.source_id,
    target: edge.target_id,
    label: edge.edge_type,
  }))
  const { positions, labelBoxes } = layoutGraph(ids, layoutEdges)
  const labelBoxByEdgeId = new Map(labelBoxes.map((box) => [box.edgeId, box]))

  const nodes = ids.map((id) => {
    const entityType = nodeTypes.get(id) ?? 'unknown'
    return buildGraphNode(id, entityType, id, names.get(id) ?? id, positions.get(id) ?? { x: 0, y: 0 })
  })

  const edges = entityEdges.map((edge) =>
    buildGraphEdge({
      id: edge.id,
      source: edge.source_id,
      target: edge.target_id,
      label: edge.edge_type,
      edgeType: edge.edge_type,
      weight: edge.weight,
      labelBox: labelBoxByEdgeId.get(edge.id),
    }),
  )

  return { nodes, edges, labelBoxes }
}

export function buildCausalityGraphModel(events: CausalEvent[]) {
  const ids = events.map((event) => getEntityNodeId('event', String(event.id))).sort()
  const eventById = new Map(events.map((event) => [event.id, event]))
  const layoutEdges: GraphLayoutEdge[] = events
    .filter((event) => event.caused_by !== null && eventById.has(event.caused_by))
    .map((event) => ({
      id: `cause:${event.caused_by}->${event.id}`,
      source: getEntityNodeId('event', String(event.caused_by)),
      target: getEntityNodeId('event', String(event.id)),
      label: 'causes',
    }))
  const { positions, labelBoxes } = layoutGraph(ids, layoutEdges)
  const labelBoxByEdgeId = new Map(labelBoxes.map((box) => [box.edgeId, box]))

  const nodes = ids.map((id) => {
    const eventID = Number(id.slice('event:'.length))
    const event = eventById.get(eventID)
    return buildGraphNode(id, event?.event_type ?? 'event', String(eventID), event?.label ?? String(eventID), positions.get(id) ?? { x: 0, y: 0 })
  })
  const edges = layoutEdges.map((edge) =>
    buildGraphEdge({
      ...edge,
      edgeType: 'causes',
      weight: 0.9,
      labelBox: labelBoxByEdgeId.get(edge.id),
    }),
  )

  return { nodes, edges, labelBoxes }
}

export interface SerializeGraphToSvgOptions {
  title?: string
  padding?: number
}

function escapeSvgText(value: unknown) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function escapeSvgAttribute(value: unknown) {
  return escapeSvgText(value).replace(/"/g, '&quot;')
}

function getNodeSize(node: Node) {
  const style = (node.style ?? {}) as CSSProperties
  const width = typeof style.width === 'number' ? style.width : NODE_W
  const height = typeof style.height === 'number' ? style.height : NODE_H
  return { width, height }
}

function getStyleColor(style: CSSProperties | undefined, key: 'background' | 'color' | 'border') {
  const value = style?.[key]
  return typeof value === 'string' ? value : undefined
}

function getMarkerId(color: string) {
  return `arrow-${color.replace(/[^a-zA-Z0-9]/g, '') || 'default'}`
}

function getIconPaths(type: string) {
  if (EVENT_NODE_COLORS[type]) return ENTITY_ICON_PATHS.event
  return ENTITY_ICON_PATHS[type] ?? ENTITY_ICON_PATHS.unknown
}

function getEntityIcon(type: string) {
  if (EVENT_NODE_COLORS[type]) return Calendar
  return ENTITY_ICONS[type] ?? ENTITY_ICONS.unknown
}

function getEdgeLabelBox(edge: Edge): GraphLabelBox | undefined {
  const data = edge.data as { labelBox?: GraphLabelBox } | undefined
  return data?.labelBox
}

function getGraphSvgMetrics(nodes: Node[], edges: Edge[], opts: SerializeGraphToSvgOptions = {}) {
  const padding = opts.padding ?? SVG_PADDING
  const initialBounds = { minX: 0, minY: 0, maxX: NODE_W, maxY: NODE_H }
  const nodeBounds = nodes.reduce(
    (acc, node) => {
      const { width, height } = getNodeSize(node)
      return {
        minX: Math.min(acc.minX, node.position.x),
        minY: Math.min(acc.minY, node.position.y),
        maxX: Math.max(acc.maxX, node.position.x + width),
        maxY: Math.max(acc.maxY, node.position.y + height),
      }
    },
    initialBounds,
  )
  const bounds = edges.reduce((acc, edge) => {
    const labelBox = getEdgeLabelBox(edge)
    if (!labelBox) return acc
    return {
      minX: Math.min(acc.minX, labelBox.x),
      minY: Math.min(acc.minY, labelBox.y),
      maxX: Math.max(acc.maxX, labelBox.x + labelBox.width),
      maxY: Math.max(acc.maxY, labelBox.y + labelBox.height),
    }
  }, nodeBounds)
  const viewBox: [number, number, number, number] = [
    bounds.minX - padding,
    bounds.minY - padding,
    bounds.maxX - bounds.minX + padding * 2,
    bounds.maxY - bounds.minY + padding * 2,
  ]
  return {
    viewBox,
    size: {
      width: viewBox[2],
      height: viewBox[3],
    },
  }
}

export function serializeGraphToSvg(
  nodes: Node<{ label: string; entityType?: string }>[],
  edges: Edge[],
  opts: SerializeGraphToSvgOptions = {},
) {
  const { viewBox, size } = getGraphSvgMetrics(nodes, edges, opts)
  const nodeById = new Map(nodes.map((node) => [node.id, node]))
  const edgeColors = [...new Set(edges.map((edge) => String(edge.style?.stroke ?? ENTITY_EDGE_STYLES.default.color)))]

  const markers = edgeColors
    .map(
      (color) => `<marker id="${getMarkerId(color)}" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="${escapeSvgAttribute(color)}" /></marker>`,
    )
    .join('')

  const edgeSvg = edges
    .map((edge) => {
      const source = nodeById.get(edge.source)
      const target = nodeById.get(edge.target)
      if (!source || !target) return ''
      const sourceSize = getNodeSize(source)
      const targetSize = getNodeSize(target)
      const sx = source.position.x + sourceSize.width / 2
      const sy = source.position.y + sourceSize.height / 2
      const tx = target.position.x + targetSize.width / 2
      const ty = target.position.y + targetSize.height / 2
      const color = String(edge.style?.stroke ?? ENTITY_EDGE_STYLES.default.color)
      const strokeWidth = Number(edge.style?.strokeWidth ?? 2)
      const labelBox = getEdgeLabelBox(edge)
      const dash =
        typeof edge.style?.strokeDasharray === 'string'
          ? ` stroke-dasharray="${escapeSvgAttribute(edge.style.strokeDasharray)}"`
          : ''
      const label = edge.label ? String(edge.label) : ''
      return [
        `<line x1="${sx}" y1="${sy}" x2="${tx}" y2="${ty}" stroke="${escapeSvgAttribute(color)}" stroke-width="${strokeWidth}"${dash} marker-end="url(#${getMarkerId(color)})" />`,
        label
          ? [
              labelBox
                ? `<rect x="${labelBox.x}" y="${labelBox.y}" width="${labelBox.width}" height="${labelBox.height}" rx="4" fill="rgba(255,255,255,0.86)" stroke="${escapeSvgAttribute(color)}" stroke-width="0.5" />`
                : '',
              `<text x="${labelBox ? labelBox.x + labelBox.width / 2 : (sx + tx) / 2}" y="${labelBox ? labelBox.y + 15 : (sy + ty) / 2 - 8}" text-anchor="middle" font-size="11" font-weight="600" fill="${escapeSvgAttribute(color)}">${escapeSvgText(label)}</text>`,
            ].join('')
          : '',
      ].join('')
    })
    .join('')

  const nodeSvg = nodes
    .map((node) => {
      const { width, height } = getNodeSize(node)
      const style = (node.style ?? {}) as CSSProperties
      const fill = getStyleColor(style, 'background') ?? ENTITY_NODE_COLORS.unknown.background
      const stroke = getStyleColor(style, 'border')?.replace(/^.*(#[0-9a-fA-F]{3,8}).*$/, '$1') ?? ENTITY_NODE_COLORS.unknown.border
      const color = getStyleColor(style, 'color') ?? ENTITY_NODE_COLORS.unknown.text
      const iconPaths = getIconPaths(String(node.data.entityType ?? 'unknown'))
      const iconX = node.position.x + 14
      const iconY = node.position.y + (height - 16) / 2
      const iconSvg = iconPaths
        .map(
          (path) =>
            `<path d="${escapeSvgAttribute(path)}" transform="translate(${iconX} ${iconY}) scale(0.6667)" fill="none" stroke="${escapeSvgAttribute(color)}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />`,
        )
        .join('')
      return [
        `<rect x="${node.position.x}" y="${node.position.y}" width="${width}" height="${height}" rx="8" fill="${escapeSvgAttribute(fill)}" stroke="${escapeSvgAttribute(stroke)}" />`,
        iconSvg,
        `<text x="${node.position.x + 38}" y="${node.position.y + height / 2 + 4}" text-anchor="start" font-size="12" font-weight="600" fill="${escapeSvgAttribute(color)}">${escapeSvgText(node.data.label)}</text>`,
      ].join('')
    })
    .join('')

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size.width}px" height="${size.height}px" viewBox="${viewBox.join(' ')}" role="img">`,
    opts.title ? `<title>${escapeSvgText(opts.title)}</title>` : '',
    `<defs>${markers}</defs>`,
    `<g>${edgeSvg}</g>`,
    `<g>${nodeSvg}</g>`,
    '</svg>',
  ].join('')
}

type EntityNodeData = { label: string; entityType: string; entityId: string }

function DagreSmoothstepEdge(props: EdgeProps<Edge>) {
  const [path] = getSmoothStepPath({
    sourceX: props.sourceX,
    sourceY: props.sourceY,
    sourcePosition: props.sourcePosition,
    targetX: props.targetX,
    targetY: props.targetY,
    targetPosition: props.targetPosition,
  })
  const labelBox = getEdgeLabelBox(props)
  const stroke = String(props.style?.stroke ?? ENTITY_EDGE_STYLES.default.color)
  const strokeWidth = Number(props.style?.strokeWidth ?? 2)

  return (
    <>
      <BaseEdge
        path={path}
        markerEnd={props.markerEnd}
        style={{
          ...props.style,
          stroke,
          strokeWidth,
        }}
      />
      {props.label && labelBox ? (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan absolute rounded px-1.5 py-0.5 text-[11px] font-semibold leading-none shadow-sm"
            style={{
              color: stroke,
              background: 'rgba(255,255,255,0.86)',
              border: `0.5px solid ${stroke}`,
              transform: `translate(-50%, -50%) translate(${labelBox.x + labelBox.width / 2}px, ${labelBox.y + labelBox.height / 2}px)`,
            }}
          >
            {props.label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  )
}

function EntityRelationNode({ data }: NodeProps<Node<EntityNodeData>>) {
  const palette = getNodePalette(data.entityType)
  const Icon = getEntityIcon(data.entityType)

  return (
    <div className="flex h-full items-center gap-2.5 px-3">
      <Handle type="target" position={Position.Left} className="opacity-0" />
      <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-background/60" aria-hidden="true">
        <Icon className="size-4" color={palette.text} strokeWidth={2.2} />
      </span>
      <span className="min-w-0 truncate text-left">{data.label}</span>
      <Handle type="source" position={Position.Right} className="opacity-0" />
    </div>
  )
}

const ENTITY_NODE_TYPES = { entity: EntityRelationNode }
const ENTITY_EDGE_TYPES = { dagreSmoothstep: DagreSmoothstepEdge }

function EntityLegend({ nodeTypes, edgeTypes }: { nodeTypes: string[]; edgeTypes: string[] }) {
  const { t } = useTranslation()
  const visibleEdgeTypes = edgeTypes.length ? edgeTypes : ['default']

  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 border-t bg-card/70 px-3 py-2 text-xs text-muted-foreground">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-foreground">{t('relationGraph.legend.nodes')}</span>
        {nodeTypes.map((type) => {
          const palette = getNodePalette(type)
          return (
            <span key={type} className="inline-flex items-center gap-1.5">
              <span className="size-2.5 rounded-full" style={{ background: palette.border }} />
              {t(`relationGraph.nodeTypes.${type}`, {
                defaultValue: t(`relationGraph.eventTypes.${type}`, { defaultValue: type }),
              })}
            </span>
          )
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-foreground">{t('relationGraph.legend.edges')}</span>
        {visibleEdgeTypes.map((type) => {
          const palette = getEdgePalette(type)
          return (
            <span key={type} className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-0 w-5 border-t-2"
                style={{ borderColor: palette.color, borderTopStyle: palette.dashed ? 'dashed' : 'solid' }}
              />
              {t(`relationGraph.edgeTypes.${type}`, { defaultValue: type })}
            </span>
          )
        })}
      </div>
    </div>
  )
}

type GraphViewMode = 'relations' | 'causality'

type SelectedGraphNode = {
  entityType: string
  entityId: string
  label: string
}

function parseCharacterAliases(value: string | null | undefined) {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) return parsed.filter((item): item is string => typeof item === 'string')
  } catch {
    // Fall through to comma parsing for legacy character rows.
  }
  return value
    .split(/[,，]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function getCharacterCanonInitialValues(character: Character | undefined, fallbackName: string): CharacterCanonFormValues {
  return {
    name: character?.name ?? fallbackName,
    aliasesText: parseCharacterAliases(character?.aliases).join(', '),
    role: character?.role ?? '',
    personality: character?.personality ?? '',
    motivation: character?.motivation ?? '',
    realm: character?.realm ?? '',
    realmLevel: character?.realm_level == null ? '' : String(character.realm_level),
    background: character?.background ?? '',
    appearance: character?.appearance ?? '',
    status: character?.status ?? '',
    notes: character?.notes ?? '',
  }
}

function DetailField({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="whitespace-pre-wrap text-sm text-foreground">{value === null || value === undefined || value === '' ? '-' : value}</p>
    </div>
  )
}

function CanonTextField({
  id,
  label,
  value,
  disabled,
  multiline = false,
  onChange,
}: {
  id: string
  label: string
  value: string
  disabled: boolean
  multiline?: boolean
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          disabled={disabled}
          rows={3}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-20 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30"
        />
      ) : (
        <Input id={id} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
      )}
    </div>
  )
}

function RelatedEdgesList({
  edges,
  names,
}: {
  edges: EntityEdge[]
  names: EntityNameIndex
}) {
  const { t } = useTranslation()
  if (!edges.length) {
    return <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">{t('relationGraph.detail.noRelatedEdges')}</p>
  }
  return (
    <div className="space-y-2">
      {edges.map((edge) => {
        const source = resolveEntityDisplayName(names, edge.source_id)
        const target = resolveEntityDisplayName(names, edge.target_id)
        const edgeType = t(`relationGraph.edgeTypes.${edge.edge_type}`, { defaultValue: edge.edge_type })
        return (
          <div key={edge.id} className="rounded-md border bg-background/40 p-3 text-sm">
            <span className="font-medium text-foreground">{source}</span>
            <span className="px-1.5 text-muted-foreground">→{edgeType}→</span>
            <span className="font-medium text-foreground">{target}</span>
          </div>
        )
      })}
    </div>
  )
}

function ForeshadowTracker({
  edges,
  names,
  isLoading,
  error,
  resolvingId,
  onResolve,
  onJumpChapter,
}: {
  edges: EntityEdge[]
  names: EntityNameIndex
  isLoading: boolean
  error: unknown
  resolvingId: string | null
  onResolve: (edge: EntityEdge) => void
  onJumpChapter: (chapter: number | null) => void
}) {
  const { t } = useTranslation()
  const sorted = sortForeshadowEdges(edges)
  if (isLoading) return <Skeleton className="h-40 w-full" />
  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
        {friendlyMessage(error, t('relationGraph.foreshadow.loadFailed'))}
      </div>
    )
  }
  if (!sorted.length) {
    return <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">{t('relationGraph.foreshadow.empty')}</p>
  }
  return (
    <div className="space-y-2">
      {sorted.map((edge) => {
        const unresolved = edge.end_chapter === null
        return (
          <div key={edge.id} className="space-y-3 rounded-md border bg-background/40 p-3">
            <div className="text-sm">{getRelatedEdgeLabel(edge, names)}</div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{t('relationGraph.foreshadow.setChapter', { chapter: edge.set_chapter ?? '-' })}</span>
              <Badge variant={unresolved ? 'destructive' : 'outline'} className="rounded-md">
                {unresolved
                  ? t('relationGraph.foreshadow.unresolved')
                  : t('relationGraph.foreshadow.resolvedAt', { chapter: edge.end_chapter })}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => onJumpChapter(edge.set_chapter)}>
                <ExternalLink className="size-4" />
                {t('relationGraph.foreshadow.jump')}
              </Button>
              {unresolved ? (
                <Button type="button" size="sm" onClick={() => onResolve(edge)} disabled={resolvingId === edge.id}>
                  {t('relationGraph.foreshadow.resolve')}
                </Button>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function EntityRelationGraph({ projectId, branchId, maxChapter = 0, onSelectEntity }: EntityRelationGraphProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [viewMode, setViewMode] = useState<GraphViewMode>('relations')
  const [chapterSnapshot, setChapterSnapshot] = useState(0)
  const [selectedNode, setSelectedNode] = useState<SelectedGraphNode | null>(null)
  const [canonValues, setCanonValues] = useState<CharacterCanonFormValues>(() => getCharacterCanonInitialValues(undefined, ''))
  const [foreshadowOpen, setForeshadowOpen] = useState(false)
  const [resolveTarget, setResolveTarget] = useState<EntityEdge | null>(null)
  const [resolveChapter, setResolveChapter] = useState('')
  const selectedChapter = maxChapter > 0 ? Math.min(chapterSnapshot, maxChapter) : 0

  const edgesQuery = useQuery({
    queryKey: ['entity-edges', projectId, selectedChapter],
    queryFn: () => fetchEntityEdges(projectId, selectedChapter > 0 ? { chapter: selectedChapter } : undefined),
    enabled: Boolean(projectId) && viewMode === 'relations',
  })

  const foreshadowQuery = useQuery({
    queryKey: ['entity-edges', projectId, 'foreshadows'],
    queryFn: () => fetchEntityEdges(projectId, { type: 'foreshadows' }),
    enabled: Boolean(projectId),
  })

  const causalityQuery = useQuery({
    queryKey: ['memory-causality', projectId, branchId ?? 'main', selectedChapter],
    queryFn: () =>
      fetchCausality(projectId, {
        ...(selectedChapter > 0 ? { chapter: selectedChapter } : {}),
        branchId,
      }),
    enabled: Boolean(projectId) && viewMode === 'causality',
  })

  const graphQuery = useQuery({
    queryKey: ['memory-graph', projectId],
    queryFn: () => fetchGraph(projectId),
    enabled: Boolean(projectId),
  })

  const characterQuery = useQuery({
    queryKey: ['character', projectId, selectedNode?.entityId],
    queryFn: () => characterApi.get(projectId, selectedNode?.entityId ?? ''),
    enabled: Boolean(projectId && selectedNode?.entityType === 'character' && selectedNode.entityId),
    retry: false,
  })

  const nameIndex = useMemo<EntityNameIndex>(() => {
    const index: EntityNameIndex = new Map()
    for (const node of graphQuery.data?.nodes ?? []) {
      if (node.id && node.label) index.set(node.id, node.label)
    }
    return index
  }, [graphQuery.data])

  const graph = useMemo(() => {
    if (viewMode === 'causality') return buildCausalityGraphModel(causalityQuery.data ?? [])
    return buildEntityRelationGraphModel(edgesQuery.data ?? [], nameIndex)
  }, [causalityQuery.data, edgesQuery.data, nameIndex, viewMode])
  const translatedEdges = useMemo(
    () =>
      graph.edges.map((edge) => ({
        ...edge,
        label: t(`relationGraph.edgeTypes.${String(edge.label ?? '')}`, { defaultValue: String(edge.label ?? '') }),
      })),
    [graph.edges, t],
  )
  const selectedEvent = useMemo(() => {
    if (!selectedNode || viewMode !== 'causality') return undefined
    return (causalityQuery.data ?? []).find((event) => String(event.id) === selectedNode.entityId)
  }, [causalityQuery.data, selectedNode, viewMode])
  const relatedEdges = useMemo(() => {
    if (!selectedNode || viewMode !== 'relations') return []
    return getNodeRelatedEdges(edgesQuery.data ?? [], selectedNode.entityType, selectedNode.entityId)
  }, [edgesQuery.data, selectedNode, viewMode])
  const foreshadowSummary = useMemo(() => getForeshadowSummary(foreshadowQuery.data ?? []), [foreshadowQuery.data])
  const nodeTypes = useMemo(
    () =>
      [
        ...new Set(
          graph.nodes.map((node) => {
            const data = node.data as { entityType?: string }
            return data.entityType ?? 'unknown'
          }),
        ),
      ].sort(),
    [graph.nodes],
  )
  const edgeTypes = useMemo(() => {
    if (viewMode === 'causality') return graph.edges.length ? ['causes'] : []
    return [...new Set((edgesQuery.data ?? []).map((edge) => edge.edge_type))].sort()
  }, [edgesQuery.data, graph.edges.length, viewMode])
  const svgExport = useMemo(() => {
    const options = { title: t(viewMode === 'causality' ? 'relationGraph.export.causalityTitle' : 'relationGraph.export.title') }
    return serializeGraphToSvg(graph.nodes, translatedEdges, options)
  }, [graph.nodes, t, translatedEdges, viewMode])

  useEffect(() => {
    if (!selectedNode || selectedNode.entityType !== 'character') return
    setCanonValues(getCharacterCanonInitialValues(characterQuery.data, selectedNode.label))
  }, [characterQuery.data, selectedNode])

  const updateCharacterMutation = useMutation({
    mutationFn: (body: CharacterInput) => characterApi.update(projectId, selectedNode?.entityId ?? '', body),
    onSuccess: async (character) => {
      toast.success(t('relationGraph.detail.saveSuccess'))
      setCanonValues(getCharacterCanonInitialValues(character, selectedNode?.label ?? character.name))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['memory-graph', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['entity-edges', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['character', projectId, selectedNode?.entityId] }),
        queryClient.invalidateQueries({ queryKey: ['characters', projectId] }),
      ])
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('relationGraph.detail.saveFailed')))
    },
  })

  const resolveForeshadowMutation = useMutation({
    mutationFn: ({ edgeId, chapter }: { edgeId: string; chapter: number }) => resolveForeshadowEdge(edgeId, chapter),
    onSuccess: async () => {
      toast.success(t('relationGraph.foreshadow.resolveSuccess'))
      setResolveTarget(null)
      setResolveChapter('')
      await queryClient.invalidateQueries({ queryKey: ['entity-edges', projectId] })
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('relationGraph.foreshadow.resolveFailed')))
    },
  })

  const updateCanonField = (key: keyof CharacterCanonFormValues, value: string) => {
    setCanonValues((current) => ({ ...current, [key]: value }))
  }

  const submitCanon = (event: FormEvent) => {
    event.preventDefault()
    if (!selectedNode || selectedNode.entityType !== 'character') return
    updateCharacterMutation.mutate(buildCharacterCanonPatch(canonValues))
  }

  const jumpToSelectedNode = () => {
    if (!selectedNode) return
    if (viewMode === 'causality') {
      if (selectedEvent?.chapter_no) onSelectEntity?.('chapter', String(selectedEvent.chapter_no))
      return
    }
    onSelectEntity?.(selectedNode.entityType, selectedNode.entityId)
  }

  const jumpToChapter = (chapter: number | null) => {
    if (chapter == null) return
    onSelectEntity?.('chapter', String(chapter))
  }

  const submitResolve = (event: FormEvent) => {
    event.preventDefault()
    const chapter = Number(resolveChapter)
    if (!resolveTarget || !Number.isFinite(chapter) || chapter <= 0) return
    resolveForeshadowMutation.mutate({ edgeId: resolveTarget.id, chapter })
  }

  const exportSvg = () => {
    const filename = viewMode === 'causality' ? 'causality-graph.svg' : 'entity-relation-graph.svg'
    downloadBlob(new Blob([svgExport], { type: 'image/svg+xml;charset=utf-8' }), filename)
  }

  const snapshotControl =
    maxChapter > 0 ? (
      <div className="flex min-w-0 flex-1 flex-col gap-2 sm:max-w-md">
        <div className="flex items-center justify-between gap-3 text-xs">
          <span className="font-medium text-foreground">{t('relationGraph.snapshot.label')}</span>
          <span className="text-muted-foreground">
            {selectedChapter > 0
              ? t('relationGraph.snapshot.chapter', { chapter: selectedChapter })
              : t('relationGraph.snapshot.all')}
          </span>
        </div>
        <Slider
          value={[selectedChapter]}
          min={0}
          max={maxChapter}
          step={1}
          onValueChange={(value) => setChapterSnapshot(value[0] ?? 0)}
          aria-label={t('relationGraph.snapshot.label')}
        />
      </div>
    ) : null

  const toolbar = (
    <div className="flex flex-col gap-3 border-b bg-card/70 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center">
        <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as GraphViewMode)}>
          <TabsList>
            <TabsTrigger value="relations">{t('relationGraph.views.relations')}</TabsTrigger>
            <TabsTrigger value="causality">{t('relationGraph.views.causality')}</TabsTrigger>
          </TabsList>
        </Tabs>
        {snapshotControl}
      </div>
      <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
        <Button type="button" variant="outline" size="sm" onClick={() => setForeshadowOpen(true)}>
          <Eye className="size-4" />
          {t('relationGraph.foreshadow.title')}
          {foreshadowSummary.unresolved > 0 ? (
            <Badge variant="destructive" className="ml-1 rounded-md px-1.5 py-0 text-[10px]">
              {foreshadowSummary.unresolved}
            </Badge>
          ) : null}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={exportSvg} disabled={!graph.nodes.length}>
          <Download className="size-4" />
          {t('relationGraph.export.svg')}
        </Button>
      </div>
    </div>
  )

  const activeQuery = viewMode === 'causality' ? causalityQuery : edgesQuery
  const activeRows = viewMode === 'causality' ? (causalityQuery.data ?? []) : (edgesQuery.data ?? [])
  const selectedTypeLabel = selectedNode
    ? t(`relationGraph.nodeTypes.${selectedNode.entityType}`, {
        defaultValue: t(`relationGraph.eventTypes.${selectedNode.entityType}`, { defaultValue: selectedNode.entityType }),
      })
    : ''
  const characterEditable = selectedNode?.entityType === 'character' && Boolean(characterQuery.data)
  const canJumpFromDetail =
    Boolean(selectedNode) && (viewMode === 'causality' || selectedNode?.entityType === 'character' || selectedNode?.entityType === 'chapter')
  const graphOverlays = (
    <>
      <Sheet open={Boolean(selectedNode)} onOpenChange={(open) => !open && setSelectedNode(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>{selectedNode?.label ?? t('relationGraph.detail.title')}</SheetTitle>
            <SheetDescription>{selectedTypeLabel}</SheetDescription>
          </SheetHeader>
          {selectedNode ? (
            <div className="space-y-5 px-4 pb-6">
              <div className="flex flex-wrap gap-2">
                {canJumpFromDetail ? (
                  <Button type="button" size="sm" variant="outline" onClick={jumpToSelectedNode}>
                    <ExternalLink className="size-4" />
                    {viewMode === 'causality' || selectedNode.entityType === 'chapter'
                      ? t('relationGraph.detail.jumpChapter')
                      : t('relationGraph.detail.jumpCharacterState')}
                  </Button>
                ) : null}
              </div>

              {viewMode === 'causality' ? (
                <div className="grid gap-3 rounded-md border bg-background/40 p-3">
                  <DetailField label={t('relationGraph.detail.eventType')} value={selectedEvent?.event_type ?? selectedNode.entityType} />
                  <DetailField label={t('relationGraph.detail.chapter')} value={selectedEvent?.chapter_no} />
                </div>
              ) : selectedNode.entityType === 'character' ? (
                <>
                  {characterQuery.isLoading ? <Skeleton className="h-32 w-full" /> : null}
                  {characterQuery.error ? (
                    <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                      {t('relationGraph.detail.characterReadOnlyFallback')}
                    </div>
                  ) : null}
                  {characterEditable ? (
                    <form className="space-y-4" onSubmit={submitCanon}>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <CanonTextField
                          id="canon-name"
                          label={t('relationGraph.detail.fields.name')}
                          value={canonValues.name}
                          disabled={updateCharacterMutation.isPending}
                          onChange={(value) => updateCanonField('name', value)}
                        />
                        <CanonTextField
                          id="canon-aliases"
                          label={t('relationGraph.detail.fields.aliases')}
                          value={canonValues.aliasesText}
                          disabled={updateCharacterMutation.isPending}
                          onChange={(value) => updateCanonField('aliasesText', value)}
                        />
                        <CanonTextField
                          id="canon-role"
                          label={t('relationGraph.detail.fields.role')}
                          value={canonValues.role}
                          disabled={updateCharacterMutation.isPending}
                          onChange={(value) => updateCanonField('role', value)}
                        />
                        <CanonTextField
                          id="canon-realm"
                          label={t('relationGraph.detail.fields.realm')}
                          value={canonValues.realm}
                          disabled={updateCharacterMutation.isPending}
                          onChange={(value) => updateCanonField('realm', value)}
                        />
                        <CanonTextField
                          id="canon-realm-level"
                          label={t('relationGraph.detail.fields.realmLevel')}
                          value={canonValues.realmLevel}
                          disabled={updateCharacterMutation.isPending}
                          onChange={(value) => updateCanonField('realmLevel', value)}
                        />
                        <CanonTextField
                          id="canon-status"
                          label={t('relationGraph.detail.fields.status')}
                          value={canonValues.status ?? ''}
                          disabled={updateCharacterMutation.isPending}
                          onChange={(value) => updateCanonField('status', value)}
                        />
                      </div>
                      <CanonTextField
                        id="canon-personality"
                        label={t('relationGraph.detail.fields.personality')}
                        value={canonValues.personality}
                        disabled={updateCharacterMutation.isPending}
                        multiline
                        onChange={(value) => updateCanonField('personality', value)}
                      />
                      <CanonTextField
                        id="canon-motivation"
                        label={t('relationGraph.detail.fields.motivation')}
                        value={canonValues.motivation}
                        disabled={updateCharacterMutation.isPending}
                        multiline
                        onChange={(value) => updateCanonField('motivation', value)}
                      />
                      <CanonTextField
                        id="canon-background"
                        label={t('relationGraph.detail.fields.background')}
                        value={canonValues.background ?? ''}
                        disabled={updateCharacterMutation.isPending}
                        multiline
                        onChange={(value) => updateCanonField('background', value)}
                      />
                      <CanonTextField
                        id="canon-appearance"
                        label={t('relationGraph.detail.fields.appearance')}
                        value={canonValues.appearance ?? ''}
                        disabled={updateCharacterMutation.isPending}
                        multiline
                        onChange={(value) => updateCanonField('appearance', value)}
                      />
                      <Button type="submit" disabled={!canonValues.name.trim() || updateCharacterMutation.isPending}>
                        <Save className="size-4" />
                        {t('relationGraph.detail.save')}
                      </Button>
                    </form>
                  ) : (
                    <div className="grid gap-3 rounded-md border bg-background/40 p-3">
                      <DetailField label={t('relationGraph.detail.fields.name')} value={selectedNode.label} />
                    </div>
                  )}
                </>
              ) : (
                <div className="grid gap-3 rounded-md border bg-background/40 p-3">
                  <DetailField label={t('relationGraph.detail.fields.name')} value={selectedNode.label} />
                  <DetailField label={t('relationGraph.detail.fields.type')} value={selectedTypeLabel} />
                </div>
              )}

              {viewMode === 'relations' ? (
                <>
                  <Separator />
                  <section className="space-y-3">
                    <h3 className="text-sm font-semibold">{t('relationGraph.detail.relatedEdges')}</h3>
                    <RelatedEdgesList edges={relatedEdges} names={nameIndex} />
                  </section>
                </>
              ) : null}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      <Sheet open={foreshadowOpen} onOpenChange={setForeshadowOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>{t('relationGraph.foreshadow.title')}</SheetTitle>
            <SheetDescription>
              {t('relationGraph.foreshadow.summary', {
                unresolved: foreshadowSummary.unresolved,
                total: foreshadowSummary.total,
              })}
            </SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-6">
            <ForeshadowTracker
              edges={foreshadowQuery.data ?? []}
              names={nameIndex}
              isLoading={foreshadowQuery.isLoading}
              error={foreshadowQuery.error}
              resolvingId={resolveForeshadowMutation.isPending ? resolveTarget?.id ?? null : null}
              onResolve={(edge) => {
                setResolveTarget(edge)
                setResolveChapter(edge.end_chapter ? String(edge.end_chapter) : '')
              }}
              onJumpChapter={jumpToChapter}
            />
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={Boolean(resolveTarget)} onOpenChange={(open) => !open && setResolveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('relationGraph.foreshadow.resolveTitle')}</DialogTitle>
            <DialogDescription>{resolveTarget ? getRelatedEdgeLabel(resolveTarget, nameIndex) : ''}</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={submitResolve}>
            <div className="space-y-2">
              <Label htmlFor="resolve-chapter">{t('relationGraph.foreshadow.resolveChapter')}</Label>
              <Input
                id="resolve-chapter"
                type="number"
                min={1}
                value={resolveChapter}
                onChange={(event) => setResolveChapter(event.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={!resolveChapter || resolveForeshadowMutation.isPending}>
                {t('relationGraph.foreshadow.resolve')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )

  if (activeQuery.isLoading) {
    return (
      <div className="overflow-hidden rounded-lg border bg-background">
        {toolbar}
        <Skeleton className="h-[520px] w-full rounded-none" />
        {graphOverlays}
      </div>
    )
  }
  if (activeQuery.error) {
    return (
      <div className="overflow-hidden rounded-lg border bg-background">
        {toolbar}
        <div className="border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {friendlyMessage(activeQuery.error, t('relationGraph.loadFailed'))}
        </div>
        {graphOverlays}
      </div>
    )
  }
  if (!activeRows.length) {
    return (
      <div className="overflow-hidden rounded-lg border bg-background">
        {toolbar}
        <div className="border-t border-dashed bg-background/45 p-6 text-center text-sm text-muted-foreground">
          {t(viewMode === 'causality' ? 'relationGraph.causalityEmpty' : 'relationGraph.empty')}
        </div>
        {graphOverlays}
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border bg-background">
      {toolbar}
      <div className="h-[calc(100vh-17rem)] min-h-[520px] w-full">
        <ReactFlow
          nodes={graph.nodes}
          edges={translatedEdges}
          nodeTypes={ENTITY_NODE_TYPES}
          edgeTypes={ENTITY_EDGE_TYPES}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          onNodeClick={(_, node) => {
            const data = node.data as { entityType?: string; entityId?: string; label?: string }
            if (data.entityType && data.entityId) {
              setSelectedNode({
                entityType: data.entityType,
                entityId: data.entityId,
                label: data.label ?? data.entityId,
              })
            }
          }}
        >
          <Background />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
      <EntityLegend nodeTypes={nodeTypes} edgeTypes={edgeTypes} />
      {graphOverlays}
    </div>
  )
}
